// greenonbooking1027 (c) — a desk "Confirm" with no patient reply on record must say HOW the patient confirmed.
// The server (AppointmentsController#confirm / #set_status → ManualConfirmGuard) refuses a click before the visit's confirm
// window with the inertia error `confirm_window` (toasted; no picker — a reason cannot override it), and a click with no
// confirmation on record with `confirm_reason` while MANUAL_CONFIRM_NEEDS_REASON is on; this helper then shows a small reason picker and re-sends
// the same PATCH with the reason. A click with a reply on record stays ONE click (no picker). Flag off: the server confirms
// and adds a warning to its notice, which is toasted as a (non-blocking) warning.
import { router } from '@inertiajs/react'
import { toast } from 'sonner'

const REASONS = [
  { key: 'phoned',       label: 'Phoned — patient confirmed' },
  { key: 'in_person',    label: 'Patient confirmed in person' },
  { key: 'whatsapp_071', label: 'Confirmed on the 071 WhatsApp' },
]
const WARNING_MARK = 'no patient reply on record'

// Resolves { key, text } or null (cancelled). Plain DOM so any page can use it without its own state.
export function pickConfirmReason(message) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div')
    overlay.className = 'fixed inset-0 z-[1000] flex items-center justify-center bg-black/40'
    const box = document.createElement('div')
    box.className = 'w-80 rounded-lg bg-white p-4 shadow-xl text-sm'
    const title = document.createElement('p')
    title.className = 'mb-3 font-medium text-gray-800'
    title.textContent = message || 'No patient reply on record - how did the patient confirm?'
    box.appendChild(title)
    const done = (val) => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(val) }
    const onKey = (e) => { if (e.key === 'Escape') done(null) }
    document.addEventListener('keydown', onKey)
    REASONS.forEach((r) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'mb-2 block w-full rounded border border-emerald-200 px-3 py-2 text-left hover:bg-emerald-50'
      b.textContent = r.label
      b.onclick = () => done({ key: r.key, text: '' })
      box.appendChild(b)
    })
    const input = document.createElement('input')
    input.type = 'text'
    input.placeholder = 'Other (type how they confirmed)'
    input.className = 'mb-3 w-full rounded border border-gray-300 px-2 py-1'
    box.appendChild(input)
    const row = document.createElement('div')
    row.className = 'flex justify-end gap-2'
    const cancel = document.createElement('button')
    cancel.type = 'button'
    cancel.className = 'rounded px-3 py-1 text-gray-600 hover:bg-gray-100'
    cancel.textContent = 'Cancel'
    cancel.onclick = () => done(null)
    const ok = document.createElement('button')
    ok.type = 'button'
    ok.className = 'rounded bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700'
    ok.textContent = 'Confirm'
    ok.onclick = () => { const t = input.value.trim(); if (t) done({ key: 'other', text: t }); else input.focus() }
    row.appendChild(cancel)
    row.appendChild(ok)
    box.appendChild(row)
    overlay.appendChild(box)
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(null) })
    document.body.appendChild(overlay)
  })
}

// router.patch with the reason round-trip. `opts` are the usual visit options (onSuccess / onError / preserveScroll …).
export function patchConfirm(url, data = {}, opts = {}) {
  const { onSuccess, onError, ...rest } = opts
  router.patch(url, data, {
    ...rest,
    onSuccess: (page) => {
      const notice = page?.props?.flash?.notice || ''
      if (notice.includes(WARNING_MARK)) toast.warning(notice)
      onSuccess?.(page)
    },
    onError: async (errors) => {
      // Before the visit's confirm window the server refuses outright (no reason can override it): say so plainly.
      if (errors?.confirm_window) { toast.error(errors.confirm_window); return }
      if (errors?.confirm_reason) {
        const picked = await pickConfirmReason(errors.confirm_reason)
        if (picked) patchConfirm(url, { ...data, confirm_reason: picked.key, confirm_reason_text: picked.text }, opts)
        return
      }
      onError?.(errors)
    },
  })
}
